/**
 * lint.ts — static graph checks (pure, domain-free).
 *
 * The conservation identity (stock = prev + inflow − outflow) is asserted per
 * conserving node at runtime; whether a node SHOULD conserve is a modeling
 * decision the engine honours blindly (`conserves: false` is the boundary
 * escape hatch). This lint upgrades that discipline to a static check: a
 * conserving INTERIOR node whose physical neighbourhood declares more than
 * one distinct unit (`GraphNode.unit`) is mixed-unit flow through a node
 * claiming conservation — the fix is `conserves: false` at that boundary or
 * splitting the node.
 *
 * Pure: no React, no DOM; mirrors sim.ts's interior/conserves rules exactly.
 */
import type { Graph, GraphNode } from './types';

const SIGNAL_KIND = 'signal';

export interface LintWarning {
  nodeId: string;
  message: string;
}

/** True when the engine's conservation check applies to this node — the same
 *  expression `sim.ts:step` uses: interior (physical in- AND out-edges) and
 *  conserving (`conserves` with the assembler kind default). */
function isConservingInterior(node: GraphNode, graph: Graph): boolean {
  let hasIn = false;
  let hasOut = false;
  for (const e of graph.edges) {
    if (e.flowKind === SIGNAL_KIND) continue;
    // conserves === false edges are display-only on the receiving side — the
    // target's inflow never receives them, so they carry no physical flow.
    if (e.conserves === false) continue;
    if (e.target === node.id) hasIn = true;
    if (e.source === node.id) hasOut = true;
  }
  if (!hasIn || !hasOut) return false;
  return node.conserves ?? node.kind !== 'assembler';
}

/** Static unit-consistency check over the graph. */
export function graphLint(graph: Graph): LintWarning[] {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const warnings: LintWarning[] = [];
  for (const node of graph.nodes) {
    if (!isConservingInterior(node, graph)) continue;
    const units = new Set<string>();
    const own = node.unit?.trim();
    if (own) units.add(own);
    for (const e of graph.edges) {
      if (e.flowKind === SIGNAL_KIND) continue;
      if (e.conserves === false) continue;
      const neighbour =
        e.target === node.id ? byId.get(e.source)
        : e.source === node.id ? byId.get(e.target)
        : undefined;
      const u = neighbour?.unit?.trim();
      if (u) units.add(u);
    }
    if (units.size > 1) {
      warnings.push({
        nodeId: node.id,
        message:
          `mixed units (${[...units].join(', ')}) at a conserving interior node — ` +
          'mark it conserves=false (an accounting boundary) or split it',
      });
    }
  }
  return warnings;
}
